"use client";

import { Button } from "@universe/ui/components/button";
import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, FileDown, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useQueryState } from "nuqs";
import { useEffect, useMemo, useRef, useState } from "react";

import BlockchainNetworkIcon, {
  getBlockchainIconPath,
} from "@/components/blockchain-network-icon";
import CopyButton from "@/components/copy-button";
import { NoTranslate } from "@/components/no-translate";
import useDownloadSettlementInstructionPdf from "@/hooks/useDownloadSettlementInstructionsPdf";
import { logError } from "@/lib/logError";
import { BlockchainSettlementMethod } from "@generated/types";

import { depositDetailsStyles as s } from "./deposit-details-styles";

// QR dimensions (px). The render reserves a fixed-size slot of exactly these
// dimensions so the loading spinner occupies the QR's footprint and the page
// does not shift when the generated QR replaces the loader. Kept as the single
// source of truth for both the generated QR and its reserved slot.
const QR_SIZE_MOBILE = 180;
const QR_SIZE_DESKTOP = 200;

// Loaded on demand: the deposit drawer is reachable from the dashboard, and a
// static import put the library in that route's first-load JS.
const loadQrCodeStyling = () =>
  import("qr-code-styling").then((module) => module.default);

interface CryptoDepositDetailsProps {
  product: string;
  settlementMethods: BlockchainSettlementMethod[];
}

export function CryptoDepositDetails({
  product,
  settlementMethods,
}: CryptoDepositDetailsProps) {
  const tBlockchain = useTranslations("blockchain");
  const tCommon = useTranslations("common");
  const tDepositDetails = useTranslations("depositDetails");
  const isMobile = useIsMobile();
  const [querySelectedAccount] = useQueryState("selectedAccount");

  // Step states
  const [currentStep, setCurrentStep] = useState<
    "chain-selection" | "wallet-display"
  >("chain-selection");
  const [selectedMethod, setSelectedMethod] =
    useState<BlockchainSettlementMethod | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const [qrImage, setQrImage] = useState<string>("");
  const [qrImageForPdf, setQrImageForPdf] = useState<string>("");
  const [isQrGenerating, setIsQrGenerating] = useState(false);

  // Group settlement methods by network to show unique networks
  const uniqueNetworks = useMemo(() => {
    const networkMap = new Map<string, BlockchainSettlementMethod>();
    settlementMethods.forEach((method) => {
      if (!networkMap.has(method.networkCode)) {
        networkMap.set(method.networkCode, method);
      }
    });
    return Array.from(networkMap.values());
  }, [settlementMethods]);

  // Helper function to load image and convert to data URL
  const loadImageAsDataUrl = async (url: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL("image/png");
            resolve(dataUrl);
          } else {
            resolve(null);
          }
        } catch (error) {
          logError("Error converting image to data URL", error, { url });
          resolve(null);
        }
      };

      img.onerror = () => {
        console.error(`Failed to load image: ${url}`);
        resolve(null);
      };

      // Add timestamp to bypass cache
      img.src = url + "?t=" + Date.now();
    });
  };

  const handleNetworkSelect = (method: BlockchainSettlementMethod) => {
    setSelectedMethod(method);
    setCurrentStep("wallet-display");
  };

  const handleChangeNetwork = () => {
    setCurrentStep("chain-selection");
    setSelectedMethod(null);
    setQrImage(""); // Clear previous QR image
    setQrImageForPdf(""); // Clear previous PDF QR image
    setIsQrGenerating(false); // Reset loading state
  };

  const { isLoading: isPdfLoading, downloadSettlementInstructionPdf } =
    useDownloadSettlementInstructionPdf({
      type: "digital-assets",
      networkTitle: tBlockchain("networkTitle"),
      networkName: selectedMethod?.network as string,
      currency: product,
      title: tBlockchain("pdfTitle", {
        currency: product,
      }),
      walletTitle: tBlockchain("walletTitle", {
        currency: product,
      }),
      walletAddress: selectedMethod?.address as string,
      qrImage: isMobile && qrImageForPdf ? qrImageForPdf : qrImage,
      qrTitle: tBlockchain("walletQrTitle"),
      warningText: tBlockchain("warningText", {
        currency: product,
      }),
      // "Good to know" bullets — mirrors the fiat settlement PDF
      explainers: [
        tBlockchain("pdfExplainers.correctNetwork", {
          currency: product,
          network: selectedMethod?.network ?? "",
        }),
        tBlockchain("pdfExplainers.irreversible"),
        tBlockchain("pdfExplainers.confirmations"),
        tBlockchain("pdfExplainers.networkFee"),
      ],
      explainersHeading: tDepositDetails("pdfExplainers.heading"),
    });

  // Generate QR Code
  useEffect(() => {
    if (selectedMethod && qrRef.current && currentStep === "wallet-display") {
      qrRef.current.innerHTML = "";
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Track QR generation state
      setIsQrGenerating(true); // Start loading

      // A network change re-runs this effect while the previous run may still
      // be awaiting the library chunk or the icon. Without this flag the stale
      // run would append the OLD address's QR into the new network's container
      // (and could land its image last for the PDF) — a wrong deposit address.
      // Checked after every await and inside every deferred callback.
      let cancelled = false;

      const generateQrCode = async () => {
        const QRCodeStyling = await loadQrCodeStyling().catch(
          (error: unknown) => {
            logError("Failed to load the QR code library", error);
            return null;
          }
        );
        if (cancelled) return;
        if (!QRCodeStyling) {
          setIsQrGenerating(false);
          return;
        }

        // Get icon path
        const iconPath = getBlockchainIconPath(selectedMethod.networkCode);

        // Use absolute URL for better Safari compatibility
        const absoluteIconPath = iconPath
          ? new URL(iconPath, window.location.origin).href
          : undefined;

        // Try to convert image to data URL first
        let imageDataUrl: string | undefined;
        if (absoluteIconPath) {
          const dataUrl = await loadImageAsDataUrl(absoluteIconPath);
          if (cancelled) return;
          if (dataUrl) {
            imageDataUrl = dataUrl;
          } else {
            console.warn(
              "Failed to convert image to data URL, generating QR without icon"
            );
          }
        }

        // Generate QR code for display
        const qrCode = new QRCodeStyling({
          width: isMobile ? QR_SIZE_MOBILE : QR_SIZE_DESKTOP,
          height: isMobile ? QR_SIZE_MOBILE : QR_SIZE_DESKTOP,
          type: "svg",
          data: selectedMethod.address,
          image: imageDataUrl,
          dotsOptions: {
            color: "#000",
            type: "rounded",
          },
          backgroundOptions: {
            color: "#fff",
          },
          imageOptions: {
            crossOrigin: "anonymous",
            margin: 10,
            imageSize: 0.5,
          },
        });

        if (qrRef.current) {
          qrCode.append(qrRef.current);

          // Use requestAnimationFrame to ensure canvas is painted
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (cancelled) return;
              qrCode
                .getRawData("png")
                .then((data) => {
                  if (cancelled) return;
                  if (data instanceof Blob) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      if (cancelled) return;
                      setQrImage(reader.result as string);

                      // If on mobile also generate a version for PDF
                      if (isMobile) {
                        const qrCodeForPdf = new QRCodeStyling({
                          width: 240,
                          height: 240,
                          type: "canvas",
                          data: selectedMethod.address,
                          image: imageDataUrl,
                          dotsOptions: {
                            color: "#000",
                            type: "rounded",
                          },
                          backgroundOptions: {
                            color: "#fff",
                          },
                        });

                        // Create temporary div to render PDF version
                        const tempDiv = document.createElement("div");
                        tempDiv.style.position = "absolute";
                        tempDiv.style.left = "-9999px";
                        document.body.appendChild(tempDiv);

                        qrCodeForPdf.append(tempDiv);

                        setTimeout(() => {
                          qrCodeForPdf
                            .getRawData("png")
                            .then((pdfData) => {
                              if (pdfData instanceof Blob) {
                                const pdfReader = new FileReader();
                                pdfReader.onloadend = () => {
                                  if (!cancelled) {
                                    setQrImageForPdf(
                                      pdfReader.result as string
                                    );
                                  }
                                  document.body.removeChild(tempDiv);
                                };
                                pdfReader.readAsDataURL(pdfData);
                              } else {
                                document.body.removeChild(tempDiv);
                              }
                            })
                            .catch(() => {
                              document.body.removeChild(tempDiv);
                            });
                        }, 100);
                      }

                      setIsQrGenerating(false); // Complete loading
                    };
                    reader.readAsDataURL(data);
                  } else {
                    setIsQrGenerating(false); // Complete loading even if no blob
                  }
                })
                .catch((error) => {
                  logError("Failed to get QR data", error);
                  if (!cancelled) setIsQrGenerating(false); // Complete loading on error
                });
            });
          });
        }
      };

      generateQrCode();

      return () => {
        cancelled = true;
      };
    }
  }, [selectedMethod, currentStep, isMobile]);

  useEffect(() => {
    if (querySelectedAccount) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset state when account changes
      setCurrentStep("chain-selection");
      setQrImage(""); // Clear previous QR image
      setQrImageForPdf(""); // Clear previous PDF QR image
      setIsQrGenerating(false); // Reset loading state
    }
  }, [querySelectedAccount]);

  // Show network selection - always show this step
  if (currentStep === "chain-selection") {
    return (
      <div
        className={cn(
          s.root,
          // Fill the drawer body so the network grid can sit mid-way between
          // the warning and the Close footer (bottom pad stays on the dialog).
          "flex flex-col items-center flex-1 min-h-0"
        )}
      >
        <p className={s.sectionTitle}>{tBlockchain("selectNetwork")}</p>
        <div className={cn(s.warningCallout, "w-full text-center")}>
          <p className={s.warningText}>{tBlockchain("verifyNetworkWarning")}</p>
        </div>
        <div className="flex-1 flex flex-col justify-center w-full self-stretch min-h-[6rem]">
          <div
            className={cn(
              "grid grid-cols-1 lg:grid-cols-2 gap-3 w-full",
              uniqueNetworks.length === 1 &&
                "lg:grid-cols-1 lg:max-w-[400px] lg:mx-auto"
            )}
          >
            {uniqueNetworks.map((method) => (
              <button
                key={method.id}
                type="button"
                onClick={() => handleNetworkSelect(method)}
                className={s.chooserCard}
              >
                <BlockchainNetworkIcon
                  network={method.networkCode}
                  size="36px"
                />
                <div className="text-left flex-1 min-w-0">
                  <p className="font-semibold text-sm md:text-base text-gray-900">
                    {method.network}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show wallet details with QR code
  if (currentStep === "wallet-display" && selectedMethod) {
    // Matches the generated QR exactly (same `isMobile` value), so the reserved
    // slot below is the QR's true footprint — no layout shift on load.
    const qrSize = isMobile ? QR_SIZE_MOBILE : QR_SIZE_DESKTOP;
    return (
      <div className={s.root}>
        <Button
          variant="outline"
          size="sm"
          onClick={handleChangeNetwork}
          className={s.backButton}
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{tDepositDetails("changeNetwork")}</span>
        </Button>

        <div className={cn(s.warningCallout, "text-center")}>
          <p className={s.warningText}>
            <span className="font-bold">{tBlockchain("verifyNetwork")}: </span>
            <span className="whitespace-pre-line">
              {tBlockchain("verifyNetworkWarningFinal")}
            </span>
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex flex-col items-center justify-center flex-1 min-h-[200px] lg:min-h-[220px] min-w-[200px] lg:min-w-[220px]">
            <p className="text-base md:text-lg font-bold text-primary mb-3">
              {selectedMethod.network}
            </p>
            {/* Fixed-size slot sized to the QR. The qrRef is empty during
                generation, so without an explicit size the slot would collapse
                and the page would jump when the QR is appended. Reserving
                `qrSize` here keeps the footprint identical before/after load,
                and scopes the loader overlay to exactly the QR's area. */}
            <div className="relative" style={{ width: qrSize, height: qrSize }}>
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, scale: 1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.75 }}
                >
                  <div ref={qrRef} />
                </motion.div>
              </AnimatePresence>
              {isQrGenerating && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-boundary" />
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-4 lg:w-1/2 justify-between">
            <div className="space-y-2">
              <p className={cn(s.fieldLabel, "text-center")}>
                {tDepositDetails("walletAddress")}
              </p>
              <div className="flex items-center gap-2 p-3 bg-brand-primary/10 rounded-lg font-mono">
                <NoTranslate className="flex-1 break-all text-xs md:text-sm font-medium text-gray-900">
                  {selectedMethod.address}
                </NoTranslate>
                <CopyButton
                  value={selectedMethod.address}
                  size="sm"
                  className="flex-shrink-0"
                />
              </div>
            </div>
            <div className={s.actions}>
              <Button
                variant="outline"
                className={cn(s.actionButton, "w-full")}
                onClick={() => {
                  if (!isQrGenerating && qrImage) {
                    downloadSettlementInstructionPdf();
                  }
                }}
                disabled={isQrGenerating || isPdfLoading || !qrImage}
              >
                <span className="inline-flex items-center justify-center">
                  {isPdfLoading || isQrGenerating ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin text-brand-boundary" />
                  ) : (
                    <FileDown className="h-4 w-4 mr-1" />
                  )}
                  <span>{tCommon("downloadPDF")}</span>
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No networks available
  return (
    <div className={cn(s.root, "text-center text-muted-foreground")}>
      {tDepositDetails("noDepositDetailsAvailable", {
        product,
      })}
    </div>
  );
}
